import { useMedicalChronology } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import { useGetSignedUrl } from "@/hooks/signedUrl";
import JSZip from "jszip";
import React, { useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";

import type { ChronologyVersion } from "../types";
import { FilesView } from "./FilesView";
import type { CaseFileRecord, CaseFileStatus, CaseFileWithStatus } from "./types";

const OCR_PROCESSING = 0;
const OCR_FAILED = 2;

const getDisplayName = (file: CaseFileRecord): string => {
  if (file.filename) return file.filename;
  if (file.bucket_url) return file.bucket_url.split("/").pop() ?? "Unknown file";
  return "Unknown file";
};

/**
 * Derives the Files-tab status badge for a case file against a chronology version.
 *
 * Priority: OCR processing/failed → no label when version or `file_ids` is
 * missing (unknown membership) → draft when the file is absent from `file_ids`
 * → the version's review status.
 */
export function getCaseFileStatus(
  file: CaseFileRecord,
  version: ChronologyVersion | null
): CaseFileStatus | null {
  if (file.processing_status === OCR_PROCESSING) return "processing";
  if (file.processing_status === OCR_FAILED) return "failed";
  if (!version) return null;
  // null/undefined = historical or never-run versions; no membership labels
  if (version.file_ids == null) return null;

  if (!version.file_ids.includes(file.file_id)) return "draft";
  return version.status;
}

export const Files: React.FC = () => {
  const {
    caseFiles,
    caseFilesLoading,
    caseFilesError,
    caseName,
    openCitation,
    versions,
    selectedVersionId,
  } = useMedicalChronology();
  const { getSignedUrl } = useGetSignedUrl();
  const selectedVersion =
    versions.find((version) => version.version_id === selectedVersionId) ?? null;
  const files = caseFiles;
  const filesWithStatus: CaseFileWithStatus[] = useMemo(
    () =>
      files.map((file) => ({
        ...file,
        status: getCaseFileStatus(file, selectedVersion),
      })),
    [files, selectedVersion]
  );

  const handleDownload = useCallback(
    async (file: CaseFileRecord) => {
      if (!file.file_id) {
        toast.error("File ID is not available for this record.");
        return;
      }

      try {
        const { signed_url: signedUrl } = await getSignedUrl(String(file.file_id));

        const response = await fetch(signedUrl);
        if (!response.ok) throw new Error("Failed to download file");

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = getDisplayName(file);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } catch (downloadError) {
        console.error("Error downloading chronology file:", downloadError);
        toast.error(`Failed to download ${getDisplayName(file)}`);
      }
    },
    [getSignedUrl]
  );

  const handleDownloadAll = useCallback(async () => {
    const downloadableFiles = files.filter((file) => Boolean(file.file_id));
    if (downloadableFiles.length === 0) {
      toast.error("No files available to download.");
      return;
    }

    const loadingToast = toast.loading("Preparing files for download...");

    try {
      const zip = new JSZip();

      await Promise.all(
        downloadableFiles.map(async (file) => {
          const { signed_url: signedUrl } = await getSignedUrl(String(file.file_id));

          const response = await fetch(signedUrl);
          if (!response.ok) throw new Error(`Unable to download ${getDisplayName(file)}`);

          const blob = await response.blob();
          zip.file(getDisplayName(file), blob);
        })
      );

      const zipContent = await zip.generateAsync({ type: "blob" });
      const objectUrl = window.URL.createObjectURL(zipContent);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${caseName || "case"} - Files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      toast.dismiss(loadingToast);
      toast.success("Files downloaded successfully.");
    } catch (downloadError) {
      console.error("Error downloading all chronology files:", downloadError);
      toast.dismiss(loadingToast);
      toast.error("Failed to download all files.");
    }
  }, [caseName, files, getSignedUrl]);

  const handlePreview = useCallback(
    (file: CaseFileRecord) => {
      void openCitation(String(file.file_id), [1]);
    },
    [openCitation]
  );

  return (
    <FilesView
      files={filesWithStatus}
      loading={caseFilesLoading}
      error={caseFilesError}
      onPreviewClick={handlePreview}
      onDownloadClick={handleDownload}
      onDownloadAllClick={handleDownloadAll}
    />
  );
};
